import type { ChangeEvent, ReactElement, RefObject } from 'react';
import { Cinputshell } from '../Cinputshell';
import { TextInputMode, type CtextinputElement, type CtextinputProps } from './Ctextinput.types';
import type { CtextinputViewModel } from './Ctextinput.view-model';
import { CtextinputChevron } from './CtextinputChevron';
import { CtextinputDropdownValue } from './CtextinputDropdownValue';
import { CtextinputErrorSlot } from './CtextinputErrorSlot';
import { CtextinputField } from './CtextinputField';

interface CtextinputBodyProps {
  autoFocus: boolean;
  backgroundColor?: CtextinputProps['backgroundColor'];
  backgroundOpacity?: number;
  borderColor?: CtextinputProps['borderColor'];
  borderOpacity?: number;
  borderWidth?: number;
  boxShadow?: string;
  disabled: boolean;
  height?: number;
  id?: string;
  innerBorder?: boolean;
  inputRef: RefObject<CtextinputElement | null>;
  name?: string;
  onContainerClick: () => void;
  onInputBlur: () => void;
  onInputChange: (event: ChangeEvent<CtextinputElement>) => void;
  onInputFocus: () => void;
  onKeyDown?: CtextinputProps['onKeyDown'];
  onScroll?: CtextinputProps['onScroll'];
  placeholder: string;
  radius?: number;
  rows: number;
  value: string;
  viewModel: CtextinputViewModel;
}

export function CtextinputBody({
  autoFocus,
  backgroundColor,
  backgroundOpacity,
  borderColor,
  borderOpacity,
  borderWidth,
  boxShadow,
  disabled,
  height,
  id,
  innerBorder,
  inputRef,
  name,
  onContainerClick,
  onInputBlur,
  onInputChange,
  onInputFocus,
  onKeyDown,
  onScroll,
  placeholder,
  radius,
  rows,
  value,
  viewModel,
}: CtextinputBodyProps): ReactElement {
  const contentByMode: Record<TextInputMode, ReactElement> = {
    [TextInputMode.Dropdown]: (
      <CtextinputDropdownValue
        displayValue={viewModel.dropdownDisplayValue}
        dropdownTextOpacity={viewModel.dropdownTextOpacity}
        textOpacity={viewModel.textOpacity}
      />
    ),
    [TextInputMode.Textual]: (
      <CtextinputField
        autoFocus={autoFocus}
        disabled={disabled}
        fieldContentStyle={viewModel.styles.fieldContentStyle}
        id={id}
        inputBaseStyle={viewModel.styles.inputBaseStyle}
        inputRef={inputRef}
        isMultiLine={viewModel.isMultiLine}
        name={name}
        onBlur={onInputBlur}
        onChange={onInputChange}
        onFocus={onInputFocus}
        onKeyDown={onKeyDown}
        onScroll={onScroll}
        placeholder={placeholder}
        rows={rows}
        textOpacity={viewModel.textOpacity}
        textareaHeight={viewModel.textareaHeight}
        value={value}
      />
    ),
  };

  return (
    <Cinputshell
      isFocused={viewModel.isShellFocused}
      isError={viewModel.isError}
      disabled={disabled}
      radius={radius}
      padding={viewModel.styles.resolvedPadding}
      backgroundColor={backgroundColor}
      backgroundOpacity={backgroundOpacity}
      borderColor={borderColor}
      borderOpacity={borderOpacity}
      borderWidth={borderWidth}
      innerBorder={innerBorder}
      boxShadow={boxShadow}
      height={height}
      justifyContent={viewModel.justifyContent}
      onClick={onContainerClick}
      containerStyle={viewModel.styles.dropdownCursorStyle}
    >
      {contentByMode[viewModel.mode]}

      <CtextinputErrorSlot
        errorText={viewModel.visibleErrorText}
        style={viewModel.styles.errorTextStyle}
      />

      <CtextinputChevron
        isVisible={viewModel.isDropdownMode}
        opacity={viewModel.chevronOpacity}
        style={viewModel.styles.chevronStyle}
      />
    </Cinputshell>
  );
}
